/**
 * Creature Canvas - Draw your creature's face with stickers
 * Ages 4-6, Touch-friendly, Stickers and drawing tools
 */

import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreatureType, SuperpowerCard } from './types';
import { CREATURE_CARDS, STICKERS } from './data';

interface CreatureCanvasProps {
  language: 'en' | 'es';
  creature: CreatureType;
  creatureName: string;
  selectedPowers: SuperpowerCard[];
  onComplete: (dataURL: string) => void;
}

const TEXT = {
  en: {
    title: 'Draw {name}!',
    subtitle: 'Draw your {creature}\'s face',
    tools: 'Drawing Tools',
    stickers: 'Stickers',
    colors: 'Colors',
    clear: 'Clear',
    undo: 'Undo',
    done: 'I\'m Done!',
    eyes: 'Eyes',
    mouths: 'Mouths',
    accessories: 'Accessories',
    decorations: 'Decorations',
    small: 'Small',
    medium: 'Medium',
    large: 'Large'
  },
  es: {
    title: '¡Dibuja a {name}!',
    subtitle: 'Dibuja la cara de tu {creature}',
    tools: 'Herramientas',
    stickers: 'Pegatinas',
    colors: 'Colores',
    clear: 'Limpiar',
    undo: 'Deshacer',
    done: '¡Terminé!',
    eyes: 'Ojos',
    mouths: 'Bocas',
    accessories: 'Accesorios',
    decorations: 'Decoraciones',
    small: 'Pequeño',
    medium: 'Mediano',
    large: 'Grande'
  }
};

const COLORS = [
  '#000000', '#EF4444', '#F59E0B', '#FBBF24', '#84CC16',
  '#10B981', '#06B6D4', '#3B82F6', '#A855F7', '#EC4899', '#F1F5F9'
];

const BRUSH_SIZES = [
  { name: 'small', size: 3 },
  { name: 'medium', size: 8 },
  { name: 'large', size: 15 }
];

type Tool = 'draw' | 'sticker';
type StickerCategory = 'eyes' | 'mouths' | 'accessories' | 'decorations';

interface PlacedSticker {
  id: string;
  emoji?: string;  // Fallback for emoji
  svg?: string;    // SVG file path
  x: number;
  y: number;
  size: number;
  isDragging?: boolean;
}

interface SelectedSticker {
  emoji?: string;
  svg?: string;
}

export default function CreatureCanvas({
  language,
  creature,
  creatureName,
  selectedPowers,
  onComplete
}: CreatureCanvasProps) {
  const [tool, setTool] = useState<Tool>('draw');
  const [color, setColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(8);
  const [stickerCategory, setStickerCategory] = useState<StickerCategory>('eyes');
  const [selectedSticker, setSelectedSticker] = useState<SelectedSticker | null>(null);
  const [placedStickers, setPlacedStickers] = useState<PlacedSticker[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [draggedSticker, setDraggedSticker] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const drawHistoryRef = useRef<ImageData[]>([]);

  const t = TEXT[language];
  const creatureData = CREATURE_CARDS.find(c => c.type === creature)!;

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = 800;
    canvas.height = 600;

    // Fill with white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Configure drawing settings
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    contextRef.current = ctx;

    // Save initial state
    saveToHistory();
  }, []);

  // Save canvas state to history
  const saveToHistory = () => {
    if (!contextRef.current || !canvasRef.current) return;
    const imageData = contextRef.current.getImageData(
      0, 0, canvasRef.current.width, canvasRef.current.height
    );
    drawHistoryRef.current.push(imageData);
    // Limit history to 20 states
    if (drawHistoryRef.current.length > 20) {
      drawHistoryRef.current.shift();
    }
  };

  // Undo last action
  const handleUndo = () => {
    if (drawHistoryRef.current.length <= 1) return;

    drawHistoryRef.current.pop(); // Remove current state
    const previousState = drawHistoryRef.current[drawHistoryRef.current.length - 1];

    if (contextRef.current && previousState) {
      contextRef.current.putImageData(previousState, 0, 0);
    }
  };

  // Clear canvas
  const handleClear = () => {
    if (!contextRef.current || !canvasRef.current) return;

    contextRef.current.fillStyle = '#FFFFFF';
    contextRef.current.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    setPlacedStickers([]);
    drawHistoryRef.current = [];
    saveToHistory();
  };

  // Helper to get canvas coordinates (accounting for scaling)
  const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const clientX = ('touches' in e) ? e.touches[0].clientX : e.clientX;
    const clientY = ('touches' in e) ? e.touches[0].clientY : e.clientY;

    // Calculate scale ratios
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Convert client coordinates to canvas coordinates
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    return { x, y };
  };

  // Start drawing
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (tool !== 'draw') return;

    const canvas = canvasRef.current;
    const ctx = contextRef.current;
    if (!canvas || !ctx) return;

    const { x, y } = getCanvasCoordinates(e);

    ctx.strokeStyle = color;
    ctx.lineWidth = brushSize;
    ctx.beginPath();
    ctx.moveTo(x, y);

    setIsDrawing(true);
  };

  // Continue drawing
  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || tool !== 'draw') return;

    const canvas = canvasRef.current;
    const ctx = contextRef.current;
    if (!canvas || !ctx) return;

    const { x, y } = getCanvasCoordinates(e);

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  // Stop drawing
  const stopDrawing = () => {
    if (isDrawing) {
      contextRef.current?.closePath();
      setIsDrawing(false);
      saveToHistory();
    }
  };

  // Place sticker (25% smaller: 480 * 0.75 = 360)
  const placeSticker = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (tool !== 'sticker' || !selectedSticker || draggedSticker) return;

    const { x, y } = getCanvasCoordinates(e);

    const newSticker: PlacedSticker = {
      id: `sticker-${Date.now()}`,
      emoji: selectedSticker.emoji,
      svg: selectedSticker.svg,
      x,
      y,
      size: 360  // 25% smaller than 480
    };

    setPlacedStickers(prev => [...prev, newSticker]);
  };

  // Helper function to render SVG sticker on canvas
  const renderSVGSticker = (svgPath: string, x: number, y: number, size: number) => {
    const ctx = contextRef.current;
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, x - size / 2, y - size / 2, size, size);
      saveToHistory();
    };
    img.src = svgPath;
  };

  // Helper function to render emoji sticker on canvas (fallback)
  const renderEmojiSticker = (emoji: string, x: number, y: number, size: number) => {
    const ctx = contextRef.current;
    if (!ctx) return;

    ctx.font = `${size}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(emoji, x, y);
  };

  // Cache for loaded images
  const imageCache = useRef<Map<string, HTMLImageElement>>(new Map());

  // Re-render all stickers when they change (optimized)
  useEffect(() => {
    if (!contextRef.current || placedStickers.length === 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Redraw from history
    if (drawHistoryRef.current.length > 0) {
      const baseImage = drawHistoryRef.current[drawHistoryRef.current.length - 1];
      contextRef.current.putImageData(baseImage, 0, 0);
    }

    // Render all stickers on top (with caching)
    const loadPromises = placedStickers.map(sticker => {
      if (sticker.svg) {
        // Check cache first
        if (imageCache.current.has(sticker.svg)) {
          const img = imageCache.current.get(sticker.svg)!;
          contextRef.current?.drawImage(
            img,
            sticker.x - sticker.size / 2,
            sticker.y - sticker.size / 2,
            sticker.size,
            sticker.size
          );
          return Promise.resolve();
        } else {
          // Load and cache
          return new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => {
              imageCache.current.set(sticker.svg!, img);
              contextRef.current?.drawImage(
                img,
                sticker.x - sticker.size / 2,
                sticker.y - sticker.size / 2,
                sticker.size,
                sticker.size
              );
              resolve();
            };
            img.src = sticker.svg!;
          });
        }
      } else if (sticker.emoji) {
        renderEmojiSticker(sticker.emoji, sticker.x, sticker.y, sticker.size);
        return Promise.resolve();
      }
      return Promise.resolve();
    });

    // Only save to history after all images are loaded
    Promise.all(loadPromises).then(() => {
      // Images loaded
    });
  }, [placedStickers]);

  // Handle sticker dragging
  const handleStickerMouseDown = (stickerId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const sticker = placedStickers.find(s => s.id === stickerId);
    if (!sticker) return;

    const { x, y } = getCanvasCoordinates(e as any);
    setDraggedSticker(stickerId);
    setDragOffset({
      x: x - sticker.x,
      y: y - sticker.y
    });
  };

  const lastDragUpdate = useRef<number>(0);
  const dragThrottle = 16; // ~60fps

  const handleStickerMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!draggedSticker) return;

    const now = Date.now();
    if (now - lastDragUpdate.current < dragThrottle) return;
    lastDragUpdate.current = now;

    const { x, y } = getCanvasCoordinates(e);

    setPlacedStickers(prev => prev.map(sticker =>
      sticker.id === draggedSticker
        ? { ...sticker, x: x - dragOffset.x, y: y - dragOffset.y }
        : sticker
    ));
  };

  const handleStickerMouseUp = () => {
    if (draggedSticker) {
      setDraggedSticker(null);
      saveToHistory();
    }
  };

  // Export canvas
  const handleDone = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataURL = canvas.toDataURL('image/png');
    onComplete(dataURL);
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col items-center p-6">
      <div className="max-w-6xl w-full">
        {/* Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-4"
        >
          <h2
            className="text-5xl md:text-6xl mb-2"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontWeight: 300,
              color: '#2a6d5b',
              letterSpacing: '-0.015em',
            }}
          >
            {t.title.replace('{name}', creatureName)}
          </h2>
          <p
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontStyle: 'italic',
              fontSize: 26,
              fontWeight: 500,
              color: '#2a6d5b',
            }}
          >
            {t.subtitle.replace('{creature}', creatureData.name[language])}
          </p>
        </motion.div>

        {/* Instruction Message */}
        {tool === 'sticker' && selectedSticker && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="px-6 py-4 mb-4 text-center"
            style={{
              background: '#42a57a',
              color: '#ffffff',
              borderRadius: 18,
              boxShadow: '0 4px 14px rgba(66,165,122,0.25)',
            }}
          >
            <p
              className="flex items-center justify-center gap-3"
              style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 22,
                fontWeight: 600,
              }}
            >
              {language === 'en' ? 'Click on the canvas to place your sticker' : 'Haz clic en el lienzo para colocar tu pegatina'}
            </p>
          </motion.div>
        )}

        {/* Canvas */}
        <div className="bg-white rounded-3xl shadow-2xl p-6 mb-6">
          <div className="relative">
            <canvas
              ref={canvasRef}
              onMouseDown={tool === 'draw' ? startDrawing : draggedSticker ? undefined : (tool === 'sticker' ? placeSticker : undefined)}
              onMouseMove={draggedSticker ? handleStickerMouseMove : (tool === 'draw' ? draw : undefined)}
              onMouseUp={draggedSticker ? handleStickerMouseUp : (tool === 'draw' ? stopDrawing : undefined)}
              onMouseLeave={tool === 'draw' ? stopDrawing : (draggedSticker ? handleStickerMouseUp : undefined)}
              onTouchStart={(e) => {
                if (tool === 'draw') {
                  startDrawing(e);
                } else if (tool === 'sticker' && !draggedSticker) {
                  const touch = e.touches[0];
                  placeSticker({ ...e, clientX: touch.clientX, clientY: touch.clientY } as any);
                }
              }}
              onTouchMove={(e) => {
                if (tool === 'draw') {
                  draw(e);
                } else if (draggedSticker) {
                  const touch = e.touches[0];
                  handleStickerMouseMove({ ...e, clientX: touch.clientX, clientY: touch.clientY } as any);
                }
              }}
              onTouchEnd={(e) => {
                if (tool === 'draw') {
                  stopDrawing();
                } else if (draggedSticker) {
                  handleStickerMouseUp();
                }
              }}
              onClick={tool === 'sticker' && !draggedSticker ? placeSticker : undefined}
              className="w-full h-auto rounded-xl cursor-crosshair touch-none bg-white"
              style={{ maxWidth: '100%', height: 'auto', aspectRatio: '4/3' }}
            />

            {/* Stickers overlay for dragging - 50% of sticker size */}
            {placedStickers.map(sticker => {
              const canvas = canvasRef.current;
              if (!canvas) return null;

              const rect = canvas.getBoundingClientRect();
              const scaleX = rect.width / canvas.width;
              const scaleY = rect.height / canvas.height;
              const isDragging = draggedSticker === sticker.id;

              // Draggable box is 50% of sticker size for better control
              const boxSizeRatio = 0.5;
              const boxWidth = sticker.size * scaleX * boxSizeRatio;
              const boxHeight = sticker.size * scaleY * boxSizeRatio;

              return (
                <div
                  key={sticker.id}
                  onMouseDown={(e) => handleStickerMouseDown(sticker.id, e)}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    const touch = e.touches[0];
                    handleStickerMouseDown(sticker.id, {
                      stopPropagation: () => {},
                      clientX: touch.clientX,
                      clientY: touch.clientY,
                    } as any);
                  }}
                  onTouchMove={(e) => {
                    if (draggedSticker === sticker.id) {
                      e.stopPropagation();
                      const touch = e.touches[0];
                      handleStickerMouseMove({ clientX: touch.clientX, clientY: touch.clientY } as any);
                    }
                  }}
                  onTouchEnd={(e) => {
                    if (draggedSticker === sticker.id) {
                      e.stopPropagation();
                      handleStickerMouseUp();
                    }
                  }}
                  className="sticker-draggable"
                  style={{
                    position: 'absolute',
                    left: `${sticker.x * scaleX}px`,
                    top: `${sticker.y * scaleY}px`,
                    transform: 'translate(-50%, -50%)',
                    cursor: tool === 'draw' ? 'crosshair' : 'move',
                    pointerEvents: tool === 'draw' ? 'none' : 'auto',
                    width: `${boxWidth}px`,
                    height: `${boxHeight}px`,
                    border: isDragging ? '3px solid #9333EA' : (tool === 'sticker' ? '2px solid rgba(147, 51, 234, 0.3)' : 'none'),
                    borderRadius: '8px',
                    backgroundColor: isDragging ? 'rgba(147, 51, 234, 0.15)' : 'transparent',
                    transition: isDragging ? 'none' : 'border 0.2s, background-color 0.2s',
                    zIndex: isDragging ? 1000 : 10,
                    opacity: tool === 'draw' ? 0 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!draggedSticker && tool === 'sticker') {
                      (e.target as HTMLElement).style.border = '2px solid #9333EA';
                      (e.target as HTMLElement).style.backgroundColor = 'rgba(147, 51, 234, 0.12)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!draggedSticker && tool === 'sticker') {
                      (e.target as HTMLElement).style.border = '2px solid rgba(147, 51, 234, 0.3)';
                      (e.target as HTMLElement).style.backgroundColor = 'transparent';
                    }
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Tool Selection — primary green pill when active, outline when inactive */}
        <div className="flex gap-3 mb-4 justify-center">
          {(['draw', 'sticker'] as const).map((toolKey) => {
            const active = tool === toolKey;
            return (
              <button
                key={toolKey}
                onClick={() => setTool(toolKey)}
                style={{
                  padding: '14px 32px',
                  borderRadius: 999,
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: 22,
                  fontWeight: 600,
                  border: '1.5px solid #42a57a',
                  background: active ? '#42a57a' : 'transparent',
                  color: active ? '#ffffff' : '#42a57a',
                  cursor: 'pointer',
                  transition: 'background 160ms ease, color 160ms ease',
                }}
              >
                {toolKey === 'draw' ? 'Draw' : 'Stickers'}
              </button>
            );
          })}
        </div>

        {/* Drawing Tools */}
        {tool === 'draw' && (
          <div
            className="p-6 mb-4"
            style={{ background: '#ffffff', border: '1px solid #e8e0c8', borderRadius: 24 }}
          >
            {/* Brush Size */}
            <div className="mb-6">
              <h3
                className="mb-3"
                style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: '#9a9a9a',
                }}
              >
                Brush Size
              </h3>
              <div className="flex gap-3 justify-center">
                {BRUSH_SIZES.map(brush => {
                  const active = brushSize === brush.size;
                  return (
                    <button
                      key={brush.name}
                      onClick={() => setBrushSize(brush.size)}
                      style={{
                        padding: '10px 22px',
                        borderRadius: 999,
                        fontFamily: "'Inter', system-ui, sans-serif",
                        fontSize: 16,
                        fontWeight: 600,
                        border: '1.5px solid #42a57a',
                        background: active ? '#42a57a' : 'transparent',
                        color: active ? '#ffffff' : '#42a57a',
                        cursor: 'pointer',
                        transition: 'background 160ms ease, color 160ms ease',
                      }}
                    >
                      {t[brush.name as keyof typeof t]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Colors */}
            <div className="mb-4">
              <h3
                className="mb-3"
                style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: '#9a9a9a',
                }}
              >
                {t.colors}
              </h3>
              <div
                className="flex gap-3 overflow-x-auto pb-4 pl-2 pr-16 pt-3"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {COLORS.map(c => {
                  const active = color === c;
                  return (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      className="flex-shrink-0"
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: c,
                        border: active ? '3px solid #2a6d5b' : '1.5px solid rgba(0,0,0,0.08)',
                        outline: active ? '4px solid rgba(66,165,122,0.25)' : 'none',
                        cursor: 'pointer',
                        transition: 'transform 160ms ease',
                        transform: active ? 'scale(1.08)' : 'scale(1)',
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Stickers */}
        {tool === 'sticker' && (
          <div
            className="p-6 mb-4"
            style={{ background: '#ffffff', border: '1px solid #e8e0c8', borderRadius: 24 }}
          >
            <h3
              className="mb-4"
              style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#9a9a9a',
              }}
            >
              {t.stickers}
            </h3>

            {/* Category Tabs */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
              {(Object.keys(STICKERS) as StickerCategory[]).map(category => {
                const active = stickerCategory === category;
                return (
                  <button
                    key={category}
                    onClick={() => setStickerCategory(category)}
                    style={{
                      padding: '10px 20px',
                      borderRadius: 999,
                      whiteSpace: 'nowrap',
                      fontFamily: "'Inter', system-ui, sans-serif",
                      fontSize: 15,
                      fontWeight: 600,
                      border: '1.5px solid #42a57a',
                      background: active ? '#42a57a' : 'transparent',
                      color: active ? '#ffffff' : '#42a57a',
                      cursor: 'pointer',
                      transition: 'background 160ms ease, color 160ms ease',
                    }}
                  >
                    {t[category]}
                  </button>
                );
              })}
            </div>

            {/* Stickers Grid */}
            <div
              className="flex gap-3 overflow-x-auto pb-3"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {STICKERS[stickerCategory].map((sticker, index) => {
                const active = selectedSticker === sticker;
                return (
                  <button
                    key={index}
                    onClick={() => setSelectedSticker(sticker)}
                    className="flex-shrink-0 flex flex-col items-center justify-center gap-1 p-2"
                    style={{
                      width: 112,
                      height: 128,
                      borderRadius: 16,
                      background: active ? '#dcecda' : '#fbfbe8',
                      border: active ? '2px solid #42a57a' : '1px solid #e8e0c8',
                      cursor: 'pointer',
                      transition: 'transform 160ms ease',
                      transform: active ? 'scale(1.04)' : 'scale(1)',
                    }}
                  >
                    {sticker.svg ? (
                      <img src={sticker.svg} alt={sticker.name[language]} className="w-14 h-14" />
                    ) : (
                      <span style={{ fontSize: 56, lineHeight: 1 }}>{sticker.emoji}</span>
                    )}
                    <span
                      className="text-center leading-tight"
                      style={{
                        fontFamily: "'Inter', system-ui, sans-serif",
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#2a6d5b',
                      }}
                    >
                      {sticker.name[language]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-4 justify-center mb-6">
          <button
            onClick={handleUndo}
            style={{
              padding: '14px 28px',
              background: 'transparent',
              color: '#42a57a',
              border: '1.5px solid #42a57a',
              borderRadius: 999,
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 18,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            ↶ {t.undo}
          </button>
          <button
            onClick={handleClear}
            style={{
              padding: '14px 28px',
              background: 'transparent',
              color: '#b85a55',
              border: '1.5px solid #b85a55',
              borderRadius: 999,
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 18,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t.clear}
          </button>
        </div>

        {/* Done Button */}
        <div className="text-center">
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleDone}
            style={{
              padding: '20px 56px',
              background: '#42a57a',
              color: '#ffffff',
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 28,
              fontWeight: 600,
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 6px 22px rgba(66,165,122,0.32)',
            }}
          >
            {t.done} ✓
          </motion.button>
        </div>
      </div>
    </div>
  );
}
